import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Fingerprint,
  KeyRound,
  Loader2,
  PencilLine,
  RefreshCw,
  XCircle,
} from "lucide-react";
import ProfileSectionCard from "./ProfileSectionCard";
import {
  generateInviteCode,
  checkInviteCodeAvailability,
  setCustomInviteCode,
} from "../../redux/profileSlice";
import { maskInviteCode, isUserVerified } from "../../utils/profileHelpers";
import { REFERRAL_UNLOCK_RULE_SHORT } from "../../utils/referralsHelpers";

const ProfileInvitePanel = ({ user, userId, onNotify }) => {
  const dispatch = useDispatch();
  const inviteCodeStatus = useSelector((s) => s.profile.inviteCodeStatus);
  const inviteCodeAvailabilityStatus = useSelector(
    (s) => s.profile.inviteCodeAvailabilityStatus
  );
  const inviteCodeAvailability = useSelector((s) => s.profile.inviteCodeAvailability);
  const generating = inviteCodeStatus === "loading";
  const checkingAvailability = inviteCodeAvailabilityStatus === "loading";

  const [isCodeVisible, setIsCodeVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [customCode, setCustomCode] = useState("");
  const availabilityTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (availabilityTimer.current) {
        clearTimeout(availabilityTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (availabilityTimer.current) {
      clearTimeout(availabilityTimer.current);
    }

    const trimmed = customCode.trim();
    if (!showCustomize || !trimmed || !userId) {
      return undefined;
    }

    availabilityTimer.current = setTimeout(() => {
      dispatch(checkInviteCodeAvailability({ id: userId, code: trimmed }));
    }, 450);

    return () => {
      if (availabilityTimer.current) {
        clearTimeout(availabilityTimer.current);
      }
    };
  }, [customCode, showCustomize, userId, dispatch]);

  if (!user) return null;

  const hasCode = Boolean(user.inviteCode);
  const displayCode = hasCode
    ? isCodeVisible
      ? user.inviteCode
      : maskInviteCode(user.inviteCode)
    : "No code yet";

  const trimmedCustomCode = customCode.trim();
  const availabilityKnown =
    inviteCodeAvailability &&
    inviteCodeAvailability.inviteCode === trimmedCustomCode.toLowerCase();
  const isAvailable = availabilityKnown && inviteCodeAvailability.available;
  const isTaken = availabilityKnown && inviteCodeAvailability.available === false;

  const copyToClipboard = async () => {
    if (!user.inviteCode) {
      onNotify?.("error", "Nothing to copy", "Generate an invite code first.");
      return;
    }

    try {
      await navigator.clipboard.writeText(user.inviteCode);
      onNotify?.("success", "Copied", "Invite code copied to clipboard.");
    } catch {
      onNotify?.("error", "Copy failed", "Could not access the clipboard.");
    }
  };

  const handleGenerate = async () => {
    if (!userId) {
      onNotify?.("error", "Generate failed", "Sign in again to manage your invite code.");
      return;
    }

    if (!isUserVerified(user)) {
      onNotify?.(
        "error",
        "Account not verified",
        "Verify your account before generating invite codes."
      );
      return;
    }

    const result = await dispatch(generateInviteCode(userId));

    if (generateInviteCode.fulfilled.match(result)) {
      setIsCodeVisible(true);
      onNotify?.(
        "success",
        hasCode ? "Code regenerated" : "Code generated",
        result.payload?.message || "Your invite code is ready to share."
      );
      return;
    }

    onNotify?.("error", "Generate failed", String(result.payload || "Try again."));
  };

  const handleSetCustomCode = async () => {
    const trimmed = customCode.trim();
    if (!trimmed || !userId) {
      onNotify?.("error", "Missing code", "Enter an invite code first.");
      return;
    }

    if (!isUserVerified(user)) {
      onNotify?.(
        "error",
        "Account not verified",
        "Verify your account before customizing invite codes."
      );
      return;
    }

    if (isTaken) {
      onNotify?.("error", "Unavailable", "This invite code is already taken.");
      return;
    }

    const result = await dispatch(setCustomInviteCode({ id: userId, inviteCode: trimmed }));

    if (setCustomInviteCode.fulfilled.match(result)) {
      setIsCodeVisible(true);
      setCustomCode(result.payload?.inviteCode || trimmed);
      onNotify?.(
        "success",
        "Code updated",
        result.payload?.message || "Your custom invite code is live."
      );
      return;
    }

    onNotify?.("error", "Update failed", String(result.payload || "Try again."));
  };

  return (
    <ProfileSectionCard
      id="referral"
      eyebrow="Refer & earn"
      title="Invitation code"
      icon={KeyRound}
      trailing={
        hasCode ? (
          <span className="prof-chip prof-chip-success">
            <span className="prof-live-dot" aria-hidden />
            Active
          </span>
        ) : null
      }
    >
      <div className="prof-code-well">
        <div className="prof-code-row">
          <Fingerprint size={14} aria-hidden />
          <code className={hasCode ? "prof-code-text" : "prof-code-text prof-code-text-empty"}>
            {displayCode}
          </code>
        </div>
        <p className="prof-code-hint">
          Share this code — claim after friends hit {REFERRAL_UNLOCK_RULE_SHORT}.
        </p>
      </div>

      <div className="prof-invite-actions">
        <button
          type="button"
          className="prof-invite-action"
          onClick={() => setIsCodeVisible((visible) => !visible)}
          disabled={!hasCode || generating}
        >
          {isCodeVisible ? <EyeOff size={12} aria-hidden /> : <Eye size={12} aria-hidden />}
          {isCodeVisible ? "Hide" : "Reveal"}
        </button>

        <button
          type="button"
          className="prof-invite-action"
          onClick={copyToClipboard}
          disabled={!hasCode || generating}
        >
          <Copy size={12} aria-hidden />
          Copy
        </button>

        <button
          type="button"
          className="prof-invite-action prof-invite-action-primary"
          onClick={handleGenerate}
          disabled={generating}
        >
          {generating ? (
            <Loader2 size={12} className="animate-spin" aria-hidden />
          ) : (
            <RefreshCw size={12} aria-hidden />
          )}
          {hasCode ? "Regenerate" : "Generate"}
        </button>
      </div>

      <button
        type="button"
        className="prof-invite-customize-toggle"
        onClick={() => setShowCustomize((visible) => !visible)}
      >
        {showCustomize ? <PencilLine size={12} aria-hidden /> : <PencilLine size={12} aria-hidden />}
        {showCustomize ? "Hide customize" : "Customize code"}
      </button>

      {showCustomize ? (
        <div className="prof-invite-customize-panel">
          <label className="prof-invite-customize-label" htmlFor="custom-invite-code">
            Choose your invite code
          </label>
          <input
            id="custom-invite-code"
            type="text"
            className="prof-invite-customize-input"
            value={customCode}
            onChange={(event) => setCustomCode(event.target.value)}
            placeholder="e.g. my-betbond-code"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <p className="prof-code-hint">
            Letters, numbers, underscores, and hyphens only. Any length.
          </p>

          {trimmedCustomCode ? (
            <div className="prof-invite-availability">
              {checkingAvailability ? (
                <>
                  <Loader2 size={12} className="animate-spin" aria-hidden />
                  <span>Checking availability…</span>
                </>
              ) : isAvailable ? (
                <>
                  <CheckCircle2 size={12} aria-hidden />
                  <span className="prof-invite-availability-available">Available</span>
                </>
              ) : isTaken ? (
                <>
                  <XCircle size={12} aria-hidden />
                  <span className="prof-invite-availability-taken">Already taken</span>
                </>
              ) : inviteCodeAvailability?.message ? (
                <span>{inviteCodeAvailability.message}</span>
              ) : null}
            </div>
          ) : null}

          <button
            type="button"
            className="prof-invite-action prof-invite-action-primary prof-invite-set-custom"
            onClick={handleSetCustomCode}
            disabled={
              !trimmedCustomCode || checkingAvailability || isTaken || generating
            }
          >
            {generating ? (
              <>
                <Loader2 size={12} className="animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              "Set custom code"
            )}
          </button>
        </div>
      ) : null}
    </ProfileSectionCard>
  );
};

export default ProfileInvitePanel;
